import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import {
  TOLL_ITINERARY_LIQUIDATION_ENDPOINT,
  TOLL_LIQUIDATION_DIFFERENCE_REASON_ENDPOINT,
  TOLL_LIQUIDATION_ENDPOINT,
} from '../../config/endpoints';
import {
  DEFAULT_GET_CONFIG,
  DEFAULT_DELETE_CONFIG,
  DEFAULT_POST_CONFIG,
  EXCEL_GET_CONFIG,
} from '../../config/rest';
import { isErrorResponse } from '../../utils/error-handlers';
import { TOLL_LIQUIDATION_PATH } from '../../config/paths';
import {
  CLEAR_ITINERARY_TOLL_LIQUIDATION,
  CLEAR_ITINERARY_TOLL_LIQUIDATION_DETAIL,
  CLEAR_TOLL_LIQUIDATION,
  CLEAR_TOLL_LIQUIDATIONS,
  FLAG_GETTING_TOLL_LIQUIDATION_DIFFENCE_REASONS,
  FLAG_GETTING_TOLL_LIQUIDATIONS,
  FLAG_ITINERARY_TOLL_LIQUIDATION_DETAIL_ACTIVITY,
  FLAG_TOLL_LIQUIDATION_ACTIVITY,
  GET_ITINERARY_TOLL_LIQUIDATION,
  GET_ITINERARY_TOLL_LIQUIDATION_DETAIL,
  GET_TOLL_LIQUIDATION,
  GET_TOLL_LIQUIDATION_DIFFENCE_REASONS,
  GET_TOLL_LIQUIDATIONS,
} from '../types';
import { DATE_FORMAT } from '../../config/locale';
import { tzNormalizeDate } from '../../utils/date';

const flagGettingTollLiquidations = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_TOLL_LIQUIDATIONS,
    payload: flag,
  });

const flagTollLiquidationActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_TOLL_LIQUIDATION_ACTIVITY,
    payload: flag,
  });

const getTollLiquidations = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(flagGettingTollLiquidations(true));
    const query = tableFilters;
    const url = `${TOLL_LIQUIDATION_ENDPOINT}?${QueryString.stringify(query)}`;
    const response = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(response, null, dispatch);
    const fuelStations = await response.json();
    dispatch({
      type: GET_TOLL_LIQUIDATIONS,
      payload: fuelStations,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingTollLiquidations(false));
  }
};

const clearTollLiquidations = () => (dispatch) =>
  dispatch({
    type: CLEAR_TOLL_LIQUIDATIONS,
  });

const getTollLiquidation =
  async ({ id }) =>
  async (dispatch) => {
    try {
      dispatch(flagTollLiquidationActivity(true));
      const url = `${TOLL_LIQUIDATION_ENDPOINT}/${id}`;
      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);
      const tollLiquidation = await response.json();
      dispatch({
        type: GET_TOLL_LIQUIDATION,
        payload: tollLiquidation,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTollLiquidationActivity(false));
    }
  };

const clearTollLiquidation = () => (dispatch) =>
  dispatch({
    type: CLEAR_TOLL_LIQUIDATION,
  });

const deleteTollLiquidation =
  async ({ id }) =>
  async (dispatch) => {
    dispatch(flagTollLiquidationActivity(true));
    try {
      const url = `${TOLL_LIQUIDATION_ENDPOINT}/${id}`;
      const response = await fetch(url, {
        ...DEFAULT_DELETE_CONFIG,
      });
      await isErrorResponse(response, null, dispatch);
      await response.json();
      dispatch(push(TOLL_LIQUIDATION_PATH));
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTollLiquidationActivity(false));
    }
  };

const postTollLiquidation =
  async ({
    itineraryId,
    totalAmount,
    tollLiquidateAmount,
    registeredBusId,
    payeeCustomerId,
    originalPayeeCustomerId,
    tollLiquidationDetailList,
    tollLiquidationAdditionalChargeList,
    differenceAmount,
    differenceAmountReasonId,
    observation,
    documentCode,
    documentSeries,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagTollLiquidationActivity(true));
      const payload = {
        itineraryId,
        totalAmount,
        tollLiquidateAmount,
        registeredBusId,
        payeeCustomerId,
        originalPayeeCustomerId,
        tollLiquidationDetailList,
        tollLiquidationAdditionalChargeList,
        differenceAmount,
        differenceAmountReasonId,
        observation,
        documentCode,
        documentSeries,
      };
      const url = TOLL_LIQUIDATION_ENDPOINT;
      const response = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(response, null, dispatch);
      const tollLiquidation = await response.json();

      return tollLiquidation;
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTollLiquidationActivity(false));
    }
    return null;
  };

const getTollItineraryLiquidation =
  async ({ companyBusId, from, to }) =>
  async (dispatch) => {
    try {
      dispatch(flagTollLiquidationActivity(true));

      const params = new URLSearchParams({
        companyBusId: String(companyBusId),
        from: String(from),
        to: String(to),
      });

      const url = `${TOLL_ITINERARY_LIQUIDATION_ENDPOINT}?${params.toString()}`;

      const response = await fetch(url, {
        ...DEFAULT_GET_CONFIG,
      });

      await isErrorResponse(response, null, dispatch);

      const itineraryTolls = await response.json();

      dispatch({
        type: GET_ITINERARY_TOLL_LIQUIDATION,
        payload: itineraryTolls,
      });

      return itineraryTolls;
    } catch ({ message }) {
      toastr.error('Error', message);
      return null;
    } finally {
      dispatch(flagTollLiquidationActivity(false));
    }
  };

const flagTollItineraryLiquidationDetailActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_ITINERARY_TOLL_LIQUIDATION_DETAIL_ACTIVITY,
    payload: flag,
  });

const getTollItineraryLiquidationDetail =
  async ({ itineraryId }) =>
  async (dispatch) => {
    try {
      dispatch(flagTollItineraryLiquidationDetailActivity(true));

      const url = `${TOLL_ITINERARY_LIQUIDATION_ENDPOINT}/detail/${itineraryId}`;

      const response = await fetch(url, {
        ...DEFAULT_GET_CONFIG,
      });

      await isErrorResponse(response, null, dispatch);

      const itineraryTollDetail = await response.json();

      dispatch({
        type: GET_ITINERARY_TOLL_LIQUIDATION_DETAIL,
        payload: itineraryTollDetail,
      });

      return itineraryTollDetail;
    } catch ({ message }) {
      toastr.error('Error', message);
      return null;
    } finally {
      dispatch(flagTollItineraryLiquidationDetailActivity(false));
    }
  };
const clearTollItineraryLiquidation = () => (dispatch) =>
  dispatch({
    type: CLEAR_ITINERARY_TOLL_LIQUIDATION,
  });

const clearTollItineraryLiquidationDetail = () => (dispatch) =>
  dispatch({
    type: CLEAR_ITINERARY_TOLL_LIQUIDATION_DETAIL,
  });

const getTollLiquidationDifferenceReasons =
  async (tableFilters) => async (dispatch) => {
    try {
      dispatch(flagGettingTollLiquidations(true));
      const query = tableFilters;
      const url = `${TOLL_LIQUIDATION_DIFFERENCE_REASON_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;
      const response = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(response, null, dispatch);
      const fuelStations = await response.json();
      dispatch({
        type: GET_TOLL_LIQUIDATION_DIFFENCE_REASONS,
        payload: fuelStations,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingTollLiquidations(false));
    }
  };

const clearTollLiquidationDifferenceReasons = () => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_TOLL_LIQUIDATION_DIFFENCE_REASONS,
  });

export const getTollLiquidationExcel =
  ({ from, to }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingTollLiquidations(true));

      const query = {
        from,
        to,
        download: true,
      };

      const url = `${TOLL_LIQUIDATION_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;

      const response = await fetch(url, EXCEL_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;

      let fileName = `reporte-peajes-${tzNormalizeDate({
        format: DATE_FORMAT,
      })}.xlsx`;

      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) {
          [, fileName] = match;
        }
      }

      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingTollLiquidations(false));
    }
  };

const getTollLiquidationAdditionalExcel =
  ({ from, to }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingTollLiquidations(true));

      const query = {
        from,
        to,
        downloadAdditional: true,
      };

      const url = `${TOLL_LIQUIDATION_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;

      const response = await fetch(url, EXCEL_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;

      let fileName = `reporte-gastos-adicionales-${tzNormalizeDate({
        format: DATE_FORMAT,
      })}.xlsx`;

      const contentDisposition = response.headers.get('Content-Disposition');

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";]+)"?/i);

        if (match) {
          [, fileName] = match;
        }
      }

      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toastr.error(
        'Error',
        error.message || 'No se pudo descargar el reporte de adicionales.',
      );
    } finally {
      dispatch(flagGettingTollLiquidations(false));
    }
  };

const getTollLiquidationProviaExcel =
  ({ from, to }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingTollLiquidations(true));

      const query = {
        from,
        to,
        downloadProvia: true,
      };

      const url = `${TOLL_LIQUIDATION_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;

      const response = await fetch(url, EXCEL_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;

      let fileName = `reporte-peajes-provias-${tzNormalizeDate({
        format: DATE_FORMAT,
      })}.xlsx`;

      const contentDisposition = response.headers.get('Content-Disposition');

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";]+)"?/i);

        if (match) {
          [, fileName] = match;
        }
      }

      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toastr.error(
        'Error',
        error.message || 'No se pudo descargar el reporte de Provías.',
      );
    } finally {
      dispatch(flagGettingTollLiquidations(false));
    }
  };

  const getTollLiquidationProviaDetailExcel =
  ({ from, to }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingTollLiquidations(true));

      const query = {
        from,
        to,
        downloadProviaDetail: true,
      };

      const url = `${TOLL_LIQUIDATION_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;

      const response = await fetch(url, EXCEL_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;

      let fileName = `reporte-peajes-provias-detalle-${tzNormalizeDate({
        format: DATE_FORMAT,
      })}.xlsx`;

      const contentDisposition = response.headers.get('Content-Disposition');

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";]+)"?/i);

        if (match) {
          [, fileName] = match;
        }
      }

      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toastr.error(
        'Error',
        error.message || 'No se pudo descargar el reporte de Provías detalle.',
      );
    } finally {
      dispatch(flagGettingTollLiquidations(false));
    }
  };

  const getTollLiquidationDetailExcel =
  ({ from, to }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingTollLiquidations(true));

      const query = {
        from,
        to,
        downloadDetail: true,
      };

      const url = `${TOLL_LIQUIDATION_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;

      const response = await fetch(url, EXCEL_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;

      let fileName = `reporte-peajes-detalle-${tzNormalizeDate({
        format: DATE_FORMAT,
      })}.xlsx`;

      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) {
          [, fileName] = match;
        }
      }

      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingTollLiquidations(false));
    }
  };

export {
  getTollLiquidations,
  clearTollLiquidations,
  getTollLiquidation,
  clearTollLiquidation,
  deleteTollLiquidation,
  postTollLiquidation,
  getTollItineraryLiquidation,
  clearTollItineraryLiquidation,
  getTollItineraryLiquidationDetail,
  clearTollItineraryLiquidationDetail,
  getTollLiquidationDifferenceReasons,
  clearTollLiquidationDifferenceReasons,
  getTollLiquidationAdditionalExcel,
  getTollLiquidationProviaExcel,
  getTollLiquidationProviaDetailExcel,
  getTollLiquidationDetailExcel
};
